import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
    Smartphone, 
    RefreshCw, 
    CheckCircle, 
    XCircle, 
    Filter,
    AlertTriangle,
    Calendar,
    Users,
    Apple,
    Play
} from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';

const AppVersionManager = () => {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const api = getAuthenticatedApi();

    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activatingId, setActivatingId] = useState(null);
    
    const [filters, setFilters] = useState({
        platform: 'all',
        target_audience: 'all'
    });

    // Carica le versioni
    const fetchVersions = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get('/app_versions.php');
            setVersions(response.data || []);
        } catch (err) {
            console.error('Errore nel caricamento delle versioni:', err);
            setError('Errore nel caricamento delle versioni');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVersions();
    }, []);

    // Attiva/disattiva una versione
    const handleToggleActive = async (versionId, currentIsActive, platform, targetAudience) => {
        if (currentIsActive) {
            // Se sta disattivando, chiedi conferma
            if (!window.confirm(t('admin.appVersions.confirmDeactivate'))) {
                return;
            }
        } else {
            // Se sta attivando, chiedi conferma e spiega cosa succederà
            if (!window.confirm(
                t('admin.appVersions.confirmActivate', {
                    platform: platform,
                    audience: targetAudience
                })
            )) {
                return;
            }
        }

        setActivatingId(versionId);
        try {
            await api.post('/app_versions.php', {
                action: 'toggle_active',
                version_id: versionId,
                is_active: currentIsActive ? 0 : 1,
                platform: platform,
                target_audience: targetAudience
            });

            // Ricarica le versioni
            await fetchVersions();
            
            alert(currentIsActive 
                ? t('admin.appVersions.deactivatedSuccess')
                : t('admin.appVersions.activatedSuccess')
            );
        } catch (err) {
            console.error('Errore nell\'attivazione/disattivazione:', err);
            alert(t('admin.appVersions.toggleError'));
        } finally {
            setActivatingId(null);
        }
    };

    // Filtra le versioni
    const filteredVersions = versions.filter(version => {
        if (filters.platform !== 'all' && version.platform !== filters.platform && version.platform !== 'both') {
            return false;
        }
        if (filters.target_audience !== 'all' && version.target_audience !== filters.target_audience && version.target_audience !== 'both') {
            return false;
        }
        return true;
    });

    // Ordina: prima le attive, poi per version_code decrescente
    const sortedVersions = [...filteredVersions].sort((a, b) => {
        // Prima ordina per is_active (attive prima)
        if (a.is_active !== b.is_active) {
            return b.is_active - a.is_active;
        }
        // Poi per version_code (più recenti prima)
        return b.version_code - a.version_code;
    });

    // Icona per la piattaforma
    const getPlatformIcon = (platform) => {
        if (platform === 'ios') return <Apple className="h-5 w-5" />;
        if (platform === 'android') return <Play className="h-5 w-5" />;
        return <Smartphone className="h-5 w-5" />;
    };

    // Badge per il target audience
    const getAudienceBadge = (audience) => {
        const colors = {
            production: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
            test: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
            both: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
        };
        
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[audience] || colors.both}`}>
                {audience === 'production' ? t('admin.appVersions.production') :
                 audience === 'test' ? t('admin.appVersions.test') :
                 t('admin.appVersions.both')}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-red-600 dark:text-red-400">{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Smartphone className="h-7 w-7" />
                        {t('admin.appVersions.title')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                        {t('admin.appVersions.description')}
                    </p>
                </div>
                <button
                    onClick={fetchVersions}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <RefreshCw className="h-4 w-4" />
                    {t('admin.appVersions.refresh')}
                </button>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                        <p className="font-semibold mb-1">{t('admin.appVersions.infoTitle')}</p>
                        <p>{t('admin.appVersions.infoDescription')}</p>
                    </div>
                </div>
            </div>

            {/* Filtri */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-3">
                    <Filter className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                        {t('admin.appVersions.filters')}
                    </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('admin.appVersions.platform')}
                        </label>
                        <select
                            value={filters.platform}
                            onChange={(e) => setFilters({ ...filters, platform: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            <option value="all">{t('admin.appVersions.allPlatforms')}</option>
                            <option value="ios">iOS</option>
                            <option value="android">Android</option>
                            <option value="both">{t('admin.appVersions.both')}</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('admin.appVersions.targetAudience')}
                        </label>
                        <select
                            value={filters.target_audience}
                            onChange={(e) => setFilters({ ...filters, target_audience: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            <option value="all">{t('admin.appVersions.allAudiences')}</option>
                            <option value="production">{t('admin.appVersions.production')}</option>
                            <option value="test">{t('admin.appVersions.test')}</option>
                            <option value="both">{t('admin.appVersions.both')}</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Lista Versioni */}
            <div className="space-y-3">
                {sortedVersions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        {t('admin.appVersions.noVersions')}
                    </div>
                ) : (
                    sortedVersions.map((version) => (
                        <div
                            key={version.id}
                            className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-2 transition-all ${
                                version.is_active 
                                    ? 'border-green-500 dark:border-green-600' 
                                    : 'border-transparent'
                            }`}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="flex items-center gap-2">
                                            {getPlatformIcon(version.platform)}
                                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                                {version.version_name}
                                            </h3>
                                        </div>
                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                            Build {version.build_number}
                                        </span>
                                        {getAudienceBadge(version.target_audience)}
                                        {version.is_active && (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                {t('admin.appVersions.active')}
                                            </span>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                            <Calendar className="h-4 w-4" />
                                            <span>
                                                {new Date(version.release_date).toLocaleDateString('it-IT', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                            <Smartphone className="h-4 w-4" />
                                            <span>
                                                {version.platform === 'ios' ? 'iOS' :
                                                 version.platform === 'android' ? 'Android' :
                                                 'iOS & Android'}
                                            </span>
                                        </div>
                                    </div>

                                    {version.update_message && (
                                        <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded text-sm text-gray-700 dark:text-gray-300">
                                            {version.update_message}
                                        </div>
                                    )}

                                    {version.update_required === 1 && (
                                        <div className="mt-2 flex items-center gap-2 text-orange-600 dark:text-orange-400 text-sm">
                                            <AlertTriangle className="h-4 w-4" />
                                            <span>{t('admin.appVersions.criticalUpdate')}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="ml-4">
                                    <button
                                        onClick={() => handleToggleActive(
                                            version.id,
                                            version.is_active === 1,
                                            version.platform,
                                            version.target_audience
                                        )}
                                        disabled={activatingId === version.id}
                                        className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                                            version.is_active
                                                ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50'
                                                : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                        {activatingId === version.id ? (
                                            <RefreshCw className="h-4 w-4 animate-spin" />
                                        ) : version.is_active ? (
                                            <>
                                                <XCircle className="h-4 w-4" />
                                                {t('admin.appVersions.deactivate')}
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle className="h-4 w-4" />
                                                {t('admin.appVersions.activate')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default AppVersionManager;
